import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { fetchPublicTeams, fetchPublicGames } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Search, Users, Play, Eye, LogOut, Plus, Star, Clock, MapPin, Calendar, Heart, X, ChevronRight, MessageSquare, AlertTriangle, Reply, ChevronDown, Send, Camera, Loader2, Flag, Ban } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import GameDayCountdown from '../components/shared/GameDayCountdown';
import InstallAppPrompt from '../components/shared/InstallAppPrompt';
import DeleteAccountDialog from '../components/shared/DeleteAccountDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const formatTime12h = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

export default function ParentBrowsePage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, isCoach, token } = useAuthStore();
  const [allTeams, setAllTeams] = useState([]);
  const [myTeams, setMyTeams] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamGames, setTeamGames] = useState([]);
  const [teamMessages, setTeamMessages] = useState([]);
  const [expandedMsgId, setExpandedMsgId] = useState(null);
  const [replyingToId, setReplyingToId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [loading, setLoading] = useState(true);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login?role=parent'); return; }
    loadMyTeams();
  }, [isAuthenticated]);

  const loadMyTeams = async () => {
    try {
      const res = await fetch(`${API}/api/parent/my-teams`, { headers });
      if (res.ok) setMyTeams(await res.json());
    } catch {} finally { setLoading(false); }
  };

  const searchTeams = async () => {
    if (!searchQuery.trim()) return;
    try {
      const data = await fetchPublicTeams(searchQuery);
      setAllTeams(data);
    } catch {}
  };

  const followTeam = async (teamId) => {
    try {
      await fetch(`${API}/api/parent/follow-team`, {
        method: 'POST', headers,
        body: JSON.stringify({ team_id: teamId })
      });
      toast.success('Team added!');
      setShowSearch(false);
      setSearchQuery('');
      loadMyTeams();
    } catch {}
  };

  const unfollowTeam = async (teamId) => {
    try {
      await fetch(`${API}/api/parent/unfollow-team`, {
        method: 'POST', headers,
        body: JSON.stringify({ team_id: teamId })
      });
      setMyTeams(prev => prev.filter(t => t.id !== teamId));
      if (selectedTeam?.id === teamId) setSelectedTeam(null);
      toast.info('Team removed');
    } catch {}
  };

  const loadTeamGames = async (teamId) => {
    try {
      const data = await fetchPublicGames(teamId);
      setTeamGames(data);
    } catch {}
  };

  const loadTeamMessages = async (teamId) => {
    try {
      const res = await fetch(`${API}/api/teams/${teamId}/messages`, { headers });
      if (res.ok) setTeamMessages(await res.json());
    } catch {}
  };

  const handleSendReply = async (messageId) => {
    if (!replyText.trim()) return;
    setReplySending(true);
    try {
      const res = await fetch(`${API}/api/messages/${messageId}/reply`, {
        method: 'POST', headers, body: JSON.stringify({ text: replyText.trim() })
      });
      if (res.ok) {
        toast.success('Reply sent!');
        setReplyingToId(null); setReplyText('');
        if (selectedTeam) loadTeamMessages(selectedTeam.id);
      }
    } catch {} finally { setReplySending(false); }
  };

  const handleReport = async (messageId, replyId, authorName) => {
    const reason = window.prompt(`Report ${replyId ? 'reply' : 'message'} from ${authorName || 'user'}. Reason?\n\n(Harassment, Hate speech, Spam, Inappropriate content, Other)`);
    if (reason === null) return;
    try {
      const res = await fetch(`${API}/api/messages/${messageId}/report`, {
        method: 'POST', headers,
        body: JSON.stringify({ reply_id: replyId || null, reason: (reason || 'not_specified').slice(0, 200) }),
      });
      if (!res.ok) throw new Error('Failed');
      const body = await res.json();
      toast.success(body.status === 'already_reported' ? 'Already reported — thanks' : 'Reported. Our team will review.');
    } catch { toast.error('Failed to submit report'); }
  };

  const handleBlock = async (targetUserId, authorName) => {
    if (!targetUserId) return;
    if (!window.confirm(`Block ${authorName || 'this user'}? You won't see their messages or replies.`)) return;
    try {
      const res = await fetch(`${API}/api/users/block`, {
        method: 'POST', headers,
        body: JSON.stringify({ target_user_id: targetUserId }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`Blocked ${authorName || 'user'}`);
      if (selectedTeam) loadTeamMessages(selectedTeam.id);
    } catch { toast.error('Failed to block user'); }
  };

  const formatRelative = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const mins = Math.floor((now - d) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleDateString();
  };

  const handleSelectTeam = (team) => {
    setSelectedTeam(team);
    loadTeamGames(team.id);
    loadTeamMessages(team.id);
    setShowMessages(false);
  };

  if (!isAuthenticated) return null;

  const myTeamIds = myTeams.map(t => t.id);
  const filteredSearch = allTeams.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) && !myTeamIds.includes(t.id)
  );

  return (
    <div className="min-h-screen bg-stone-950 p-4 md:p-6" data-testid="parent-browse-page">
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Eye size={24} className="text-amber-500" />
            <div>
              <h1 className="text-2xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                PARENT HUB
              </h1>
              <p className="text-stone-500 text-xs">Welcome, {user?.username}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isCoach && (
              <Button size="sm" onClick={() => navigate('/dashboard')} className="bg-green-700 hover:bg-green-600 text-xs" data-testid="coach-dashboard-btn">
                Coach View
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }} className="text-stone-400" data-testid="logout-btn">
              <LogOut size={14} />
            </Button>
            <DeleteAccountDialog />
          </div>
        </div>

        {/* Install App Prompt - helps parents add app to homescreen */}
        <InstallAppPrompt variant="banner" />

        {/* Game Day Countdowns for all followed teams */}
        {myTeams.filter(t => t.next_game).map(team => (
          <GameDayCountdown
            key={team.id}
            games={team.next_game ? [{ ...team.next_game, is_complete: false }] : []}
            teamName={team.name}
          />
        ))}

        {/* My Teams */}
        <Card className="border-0 bg-stone-900" data-testid="my-teams-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                <Heart size={14} className="text-red-400" /> MY TEAMS ({myTeams.length})
              </CardTitle>
              <Button size="sm" variant="outline" className="text-xs border-amber-600 text-amber-400 hover:bg-amber-900/30 h-7"
                data-testid="add-team-btn"
                onClick={() => { setShowSearch(!showSearch); if (!showSearch) searchTeams(); }}>
                <Plus size={12} className="mr-1" /> Add Team
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* Search / Add Team */}
            {showSearch && (
              <div className="bg-stone-800 rounded-lg p-3 space-y-2 border border-stone-700" data-testid="team-search-panel">
                <div className="flex gap-2">
                  <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && searchTeams()}
                    placeholder="Search teams by name..."
                    className="bg-stone-700 border-stone-600 text-white text-sm"
                    data-testid="team-search-input" />
                  <Button size="sm" onClick={searchTeams} className="bg-amber-600 hover:bg-amber-500">
                    <Search size={14} />
                  </Button>
                </div>
                {filteredSearch.length > 0 && (
                  <div className="space-y-1 max-h-[200px] overflow-y-auto">
                    {filteredSearch.map(team => (
                      <div key={team.id} className="flex items-center justify-between bg-stone-700/50 rounded px-3 py-2">
                        <div>
                          <p className="text-white text-sm font-semibold">{team.name}</p>
                          <p className="text-stone-400 text-[10px]">{team.sport_type} - {team.player_count} players</p>
                        </div>
                        <Button size="sm" className="bg-green-700 hover:bg-green-600 text-xs h-7"
                          data-testid={`follow-team-${team.id}`}
                          onClick={() => followTeam(team.id)}>
                          <Plus size={12} className="mr-1" /> Follow
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {searchQuery && filteredSearch.length === 0 && allTeams.length > 0 && (
                  <p className="text-stone-500 text-xs text-center py-2">No new teams found matching "{searchQuery}"</p>
                )}
              </div>
            )}

            {/* Teams List */}
            {loading ? (
              <div className="flex justify-center py-6">
                <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : myTeams.length === 0 ? (
              <div className="text-center py-8">
                <Users size={32} className="mx-auto text-stone-700 mb-2" />
                <p className="text-stone-400 text-sm">No teams followed yet</p>
                <p className="text-stone-600 text-xs mt-1">Search and add your kid's team above</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="my-teams-list">
                {myTeams.map(team => (
                  <div key={team.id}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all border",
                      selectedTeam?.id === team.id
                        ? "bg-amber-900/30 border-amber-600"
                        : "bg-stone-800/50 border-stone-700 hover:border-stone-600"
                    )}
                    onClick={() => handleSelectTeam(team)}
                    data-testid={`my-team-${team.id}`}>
                    <div className="w-10 h-10 rounded-lg bg-amber-700/30 flex items-center justify-center shrink-0">
                      <Users size={18} className="text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-sm truncate">{team.name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-stone-500">
                        <span>{team.sport_type}</span>
                        <span>{team.player_count} players</span>
                        <span>{team.game_count} games</span>
                        {team.schedule_count > 0 && <span className="text-green-500">{team.schedule_count} upcoming</span>}
                      </div>
                      {team.next_game && (
                        <div className="flex items-center gap-2 text-[10px] text-amber-400 mt-0.5">
                          <Calendar size={8} />
                          <span>Next: vs {team.next_game.opponent_name} - {team.next_game.date}</span>
                          {team.next_game.game_time && <span>{formatTime12h(team.next_game.game_time)}</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={e => { e.stopPropagation(); unfollowTeam(team.id); }}
                        className="text-stone-600 hover:text-red-400 p-1" title="Unfollow">
                        <X size={14} />
                      </button>
                      <ChevronRight size={16} className="text-stone-600" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Selected Team Games */}
        {selectedTeam && (
          <>
          {/* Tab Toggle: Games / Messages */}
          <div className="flex gap-2">
            <Button size="sm" variant={!showMessages ? 'default' : 'outline'}
              className={!showMessages ? 'bg-green-700' : 'border-stone-600 text-stone-300'}
              onClick={() => setShowMessages(false)} data-testid="tab-games">
              <Play size={14} className="mr-1" /> Games
            </Button>
            <Button size="sm" variant={showMessages ? 'default' : 'outline'}
              className={showMessages ? 'bg-amber-600' : 'border-stone-600 text-stone-300'}
              onClick={() => setShowMessages(true)} data-testid="tab-messages">
              <MessageSquare size={14} className="mr-1" /> Messages
              {teamMessages.filter(m => m.is_urgent).length > 0 && (
                <span className="ml-1 bg-red-500 text-white text-[9px] px-1.5 rounded-full font-bold">
                  {teamMessages.filter(m => m.is_urgent).length}
                </span>
              )}
            </Button>
          </div>

          {!showMessages ? (
          <Card className="border-0 bg-stone-900" data-testid="team-games-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  <Play size={14} className="inline mr-2 text-green-400" />
                  {selectedTeam.name} GAMES
                </CardTitle>
                <Button variant="ghost" size="sm" className="text-stone-400 text-xs" onClick={() => setSelectedTeam(null)}>
                  Close
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {teamGames.length === 0 ? (
                <p className="text-stone-500 text-sm text-center py-6">No games yet for this team</p>
              ) : (
                <div className="space-y-2">
                  {teamGames.map(game => (
                    <div key={game.id}
                      className="flex items-center justify-between bg-stone-800/50 rounded-lg px-4 py-3 cursor-pointer hover:bg-stone-700/50 transition-all"
                      onClick={() => navigate(`/parent-view/${game.team_id}/${game.id}`)}
                      data-testid={`game-item-${game.id}`}>
                      <div>
                        <p className="text-white font-bold text-sm">
                          {game.is_home ? 'vs' : '@'} {game.opponent_name}
                        </p>
                        <div className="flex items-center gap-2 text-[10px] text-stone-500">
                          <span>{game.date}</span>
                          {game.game_time && <span className="flex items-center gap-0.5"><Clock size={8} /> {formatTime12h(game.game_time)}</span>}
                          {game.field_location && <span className="flex items-center gap-0.5"><MapPin size={8} /> {game.field_location}</span>}
                        </div>
                      </div>
                      <div>
                        {game.is_complete ? (
                          <span className="text-white font-bold text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                            {game.home_score}-{game.away_score}
                          </span>
                        ) : (
                          <span className="bg-green-600 text-white px-2 py-0.5 rounded text-[10px] font-bold animate-pulse">LIVE</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          ) : (
          /* Team Messages for Parents */
          <Card className="border-0 bg-stone-900" data-testid="team-messages-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                <MessageSquare size={14} className="inline mr-2 text-amber-400" />
                {selectedTeam.name} MESSAGES
              </CardTitle>
            </CardHeader>
            <CardContent>
              {teamMessages.length === 0 ? (
                <p className="text-stone-500 text-sm text-center py-6">No messages from the coach yet</p>
              ) : (
                <div className="space-y-3">
                  {teamMessages.map(msg => (
                    <div key={msg.id} className={cn(
                      "rounded-lg p-3 transition-all",
                      msg.is_urgent ? "bg-red-900/30 border border-red-700" : "bg-stone-800/50"
                    )} data-testid={`parent-msg-${msg.id}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-6 h-6 rounded-full bg-green-700 text-white flex items-center justify-center text-[10px] font-bold">
                          {msg.author_name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <span className="text-white text-xs font-semibold">{msg.author_name}</span>
                        {msg.is_urgent && (
                          <span className="text-[9px] bg-red-600 text-white px-1.5 py-0.5 rounded font-bold flex items-center gap-0.5">
                            <AlertTriangle size={7} /> URGENT
                          </span>
                        )}
                        <span className="text-stone-500 text-[10px] ml-auto">{formatRelative(msg.created_at)}</span>
                        {msg.author_id && msg.author_id !== user?.id && (
                          <>
                            <button
                              onClick={() => handleReport(msg.id, null, msg.author_name)}
                              className="text-stone-500 hover:text-red-400 ml-1"
                              title="Report message"
                              data-testid={`parent-report-msg-${msg.id}`}>
                              <Flag size={11} />
                            </button>
                            <button
                              onClick={() => handleBlock(msg.author_id, msg.author_name)}
                              className="text-stone-500 hover:text-red-400"
                              title={`Block ${msg.author_name}`}
                              data-testid={`parent-block-msg-${msg.id}`}>
                              <Ban size={11} />
                            </button>
                          </>
                        )}
                      </div>
                      {msg.text && <p className="text-stone-300 text-sm whitespace-pre-wrap">{msg.text}</p>}
                      {msg.photo && <img src={msg.photo} alt="" className="rounded-lg max-h-48 mt-2 border border-stone-700" />}

                      {/* Replies */}
                      <div className="mt-2 flex items-center gap-3">
                        <button className="text-[10px] text-stone-500 hover:text-amber-400 flex items-center gap-1"
                          onClick={() => setReplyingToId(replyingToId === msg.id ? null : msg.id)}
                          data-testid={`parent-reply-btn-${msg.id}`}>
                          <Reply size={10} /> Reply
                        </button>
                        {msg.replies?.length > 0 && (
                          <button className="text-[10px] text-stone-500 hover:text-stone-300 flex items-center gap-1"
                            onClick={() => setExpandedMsgId(expandedMsgId === msg.id ? null : msg.id)}>
                            <ChevronDown size={10} className={cn("transition-transform", expandedMsgId === msg.id && "rotate-180")} />
                            {msg.replies.length} {msg.replies.length === 1 ? 'reply' : 'replies'}
                          </button>
                        )}
                      </div>

                      {expandedMsgId === msg.id && msg.replies?.length > 0 && (
                        <div className="mt-2 space-y-1.5 pl-4 border-l-2 border-stone-700">
                          {msg.replies.map(r => (
                            <div key={r.id} className="bg-stone-800 rounded px-3 py-2">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-white text-[10px] font-semibold">{r.author_name}</span>
                                <span className="text-stone-500 text-[9px]">{formatRelative(r.created_at)}</span>
                                {r.author_id && r.author_id !== user?.id && (
                                  <button
                                    onClick={() => handleReport(msg.id, r.id, r.author_name)}
                                    className="text-stone-500 hover:text-red-400 ml-auto"
                                    title="Report reply"
                                    data-testid={`parent-report-reply-${r.id}`}>
                                    <Flag size={10} />
                                  </button>
                                )}
                              </div>
                              {r.text && <p className="text-stone-400 text-xs">{r.text}</p>}
                              {r.photo && <img src={r.photo} alt="" className="rounded max-h-24 mt-1 border border-stone-700" />}
                            </div>
                          ))}
                        </div>
                      )}

                      {replyingToId === msg.id && (
                        <div className="mt-2 flex gap-2" data-testid={`parent-reply-form-${msg.id}`}>
                          <input value={replyText} onChange={e => setReplyText(e.target.value)}
                            placeholder="Write a reply..."
                            className="flex-1 bg-stone-800 border border-stone-600 rounded px-3 py-1.5 text-sm text-white placeholder:text-stone-500"
                            onKeyDown={e => e.key === 'Enter' && handleSendReply(msg.id)}
                            data-testid={`parent-reply-input-${msg.id}`} />
                          <Button size="sm" disabled={replySending} className="bg-amber-600 hover:bg-amber-500 px-3"
                            onClick={() => handleSendReply(msg.id)} data-testid={`parent-send-reply-${msg.id}`}>
                            {replySending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          )}
          </>
        )}
      </div>
    </div>
  );
}
